import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import {
  useIncidentDetail,
  useIncidentAppleEedCapabilities,
  useIncidentLiveState,
  usePortalHub,
} from '@ngsos/portal-core/portal';

import { LoadingFailedBox, NgPageHeader } from '@/ui';
import { useAgencyConfiguration } from '@/app/shell/agencyConfiguration';
import {
  canUseAppleEedVideoActions,
  getAgencyView,
  getQueryParamCaseInsensitive,
  isAppleEedLiveVideo,
} from '@ngsos/portal-core/incidents';
import { CollaborationState, IncidentState, isCoordinateLocation } from '@/signalr';
import { openPhoneNumberDialog } from './actions';
import { IncidentDetailDialogs } from './dialogs/IncidentDetailDialogs';
import { createIncidentRealtime, IncidentRealtimeContext } from '@ngsos/portal-core/chat';
import { ActionsMobileMenuProvider } from './layout/actionsMobileMenu';
import { IncidentDetailLayoutBox } from './layout/IncidentDetailLayoutBox';
import { getInitialIncidentPanel } from './layout/incidentPanel';
import { IncidentBanner } from './banners/IncidentBanner';
import { IncidentDetailCollaborationStateBanner } from './banners/IncidentDetailCollaborationStateBanner';
import { IncidentDataPanel } from './IncidentDataPanel';
import { IncidentDetailActionBar } from './IncidentDetailActionBar';
import { IncidentDetailMap } from './IncidentDetailMap';
import { IncidentNotFoundPage } from './IncidentNotFoundPage';
import {
  IncidentDetailMapLoadingPlaceholder,
  IncidentDetailPanelLoadingPlaceholder,
} from './IncidentDetailLoadingPlaceholders';
import { IncidentChatPlaceholder } from './placeholders/IncidentChatPlaceholder';
import { VideoBoxPlaceholder } from './placeholders/VideoBoxPlaceholder';

export type IncidentDetailPageProps = {
  incidentId: string;
};

export function IncidentDetailPage({ incidentId }: IncidentDetailPageProps) {
  const { t } = useTranslation();
  const agencyConfiguration = useAgencyConfiguration();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const hub = usePortalHub();
  const { incident, status, retry } = useIncidentDetail(incidentId);
  const { callerConnectedStatus } = useIncidentLiveState(incidentId);

  const realtime = useMemo(() => (hub === null ? null : createIncidentRealtime(hub, incidentId)), [hub, incidentId]);

  const callerPhoneNumbers = incident?.caller.phoneNumbers ?? [];
  const appleEedCallCapabilities = useIncidentAppleEedCapabilities(incidentId);

  const isLoading = incident === null;

  const state = incident?.state ?? null;
  const lastCallerCoordinate =
    incident?.lastPosition != null && isCoordinateLocation(incident.lastPosition) ? incident.lastPosition : null;
  const hasPhoneNumber = callerPhoneNumbers.length > 0;

  const agencyView = incident !== null ? getAgencyView(incident, agencyConfiguration.id) : null;
  const incidentCollaborationState = agencyView?.collaborationState ?? CollaborationState.Supervising;
  const incidentIsReadOnly = state !== IncidentState.Open || incidentCollaborationState === CollaborationState.Declined;
  const allowAppleEedVideoActions = canUseAppleEedVideoActions(
    agencyConfiguration.appleEedAllowMedicalData,
    incidentIsReadOnly,
  );

  const liveVideos = incident?.caller.liveVideos ?? [];
  const activeLiveVideo = liveVideos.find((video) => video.id === incident?.activeLiveVideoId) ?? null;
  const activeAppleEedVideo = isAppleEedLiveVideo(activeLiveVideo) ? activeLiveVideo : null;
  const hasActiveAppleEedVideo = activeAppleEedVideo !== null;
  const hasAppleEedVideoCapability = appleEedCallCapabilities.some((capability) => capability.hasVideo);
  const appleEedVideoCallId = activeAppleEedVideo?.callId ?? null;
  const initialActivePanel = incident === null ? null : getInitialIncidentPanel(activeLiveVideo !== null);

  const [videoExpanded, setVideoExpanded] = useState(false);

  const phoneNumberDialogHandledRef = useRef(false);
  const openPhoneNumberDialogParam = getQueryParamCaseInsensitive(searchParams, 'OpenPhoneNumberDialog');
  useEffect(() => {
    if (isLoading || phoneNumberDialogHandledRef.current) {
      return;
    }
    if (openPhoneNumberDialogParam?.toLowerCase() === 'true') {
      phoneNumberDialogHandledRef.current = true;
      openPhoneNumberDialog(incidentId);
      void navigate(`/incident/${incidentId}`);
    }
  }, [isLoading, openPhoneNumberDialogParam, incidentId, navigate]);

  if (status === 'not-found') {
    return <IncidentNotFoundPage />;
  }

  if (status === 'failed') {
    return (
      <div className="content p-3">
        <NgPageHeader title={t('Incident_PageTitle')} />
        <LoadingFailedBox onRetry={retry} />
      </div>
    );
  }

  return (
    <>
      <NgPageHeader title={t('Incident_PageTitle')} />

      <IncidentRealtimeContext.Provider value={realtime}>
        <ActionsMobileMenuProvider>
          <IncidentDetailLayoutBox
            incidentState={state}
            initialActivePanel={initialActivePanel}
            hasActiveAppleEedVideo={hasActiveAppleEedVideo}
            videoExpanded={videoExpanded}
            dataPanelArea={
              isLoading ? (
                <IncidentDetailPanelLoadingPlaceholder />
              ) : incident !== null ? (
                <IncidentDataPanel
                  incident={incident}
                  incidentIsReadOnly={incidentIsReadOnly}
                  appleEedCallCapabilities={appleEedCallCapabilities}
                />
              ) : null
            }
            bannersArea={
              <>
                {state === IncidentState.Closed ? (
                  <IncidentBanner>{t('Incident_IncidentClosedStatusLabel')}</IncidentBanner>
                ) : (
                  incident !== null && (
                    <IncidentDetailCollaborationStateBanner
                      incidentId={incidentId}
                      collaborationState={incidentCollaborationState}
                    />
                  )
                )}
              </>
            }
            actionBarArea={
              !isLoading && (
                <IncidentDetailActionBar
                  incidentIsReadOnly={incidentIsReadOnly}
                  incidentId={incidentId}
                  involvedAgencies={incident?.involvedAgencies}
                />
              )
            }
            mapArea={
              isLoading ? (
                <IncidentDetailMapLoadingPlaceholder />
              ) : (
                <IncidentDetailMap
                  incidentId={incidentId}
                  callerPosition={incident?.lastPosition ?? null}
                  callerPositionHistory={incident?.positions ?? []}
                  lastPositionBySource={incident?.lastPositionBySource ?? []}
                  permanentResidence={incident?.caller.permanentResidence}
                  incidentIsReadOnly={incidentIsReadOnly}
                />
              )
            }
            videoPanelArea={
              <VideoBoxPlaceholder
                incidentId={incidentId}
                incidentState={state}
                incidentIsReadOnly={incidentIsReadOnly}
                incidentHasPhoneNumber={hasPhoneNumber}
                hasAppleEedVideoCapability={hasAppleEedVideoCapability}
                allowAppleEedVideoActions={allowAppleEedVideoActions}
                appleEedVideoCallId={appleEedVideoCallId}
                liveVideos={liveVideos}
                activeLiveVideoId={incident?.activeLiveVideoId ?? null}
                videoExpanded={videoExpanded}
                onVideoExpandedChanged={setVideoExpanded}
                callerConnectedStatus={callerConnectedStatus}
              />
            }
            chatArea={
              <IncidentChatPlaceholder
                incidentId={incidentId}
                callerConnectedStatus={callerConnectedStatus}
                incidentHasPhoneNumber={hasPhoneNumber}
                incidentIsReadOnly={incidentIsReadOnly}
                hasActiveAppleEedVideo={hasActiveAppleEedVideo}
                videoExpanded={videoExpanded}
                onRequestVideoCollapse={() => setVideoExpanded(false)}
              />
            }
          />

          <IncidentDetailDialogs
            incidentId={incidentId}
            lastCallerPosition={lastCallerCoordinate}
            involvedAgencies={incident?.involvedAgencies}
          />
        </ActionsMobileMenuProvider>
      </IncidentRealtimeContext.Provider>
    </>
  );
}
